import { useState, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Upload, FileUp, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { parseOrderCSV, ParsedTrip, LocationRecord, CustomerRecord, TractorRecord, DriverRecord, TrailerTypeRecord } from "./csvOrderParser";

interface ImportOrdersDialogProps {
  onImport: (trips: ParsedTrip[]) => void;
  isImporting: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export function ImportOrdersDialog({ onImport, isImporting, open: openProp, onOpenChange, hideTrigger }: ImportOrdersDialogProps) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    if (openProp === undefined) setOpenState(v);
  };
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedTrips, setParsedTrips] = useState<ParsedTrip[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: locations } = useQuery({
    queryKey: ["locations-for-import"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("id, short_code, customer_id, street_address, city, province_state, postal_zip_code, country")
        .eq("archived", false);
      if (error) throw error;
      return data as LocationRecord[];
    },
  });

  const { data: customers } = useQuery({
    queryKey: ["customers-for-import"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, short_code, organization_name");
      if (error) throw error;
      return data as CustomerRecord[];
    },
  });

  const { data: tractors } = useQuery({
    queryKey: ["tractors-for-import"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractors")
        .select("id, name");
      if (error) throw error;
      return data as TractorRecord[];
    },
  });

  const { data: drivers } = useQuery({
    queryKey: ["drivers-for-import"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select("id, name, email");
      if (error) throw error;
      return data as DriverRecord[];
    },
  });

  const { data: trailerTypes } = useQuery({
    queryKey: ["trailer-types-for-import"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailer_types")
        .select("id, slug, label");
      if (error) throw error;
      return data as TrailerTypeRecord[];
    },
  });

  const reset = () => {
    setFileName(null);
    setParsedTrips(null);
    setError(null);
    setIsParsing(false);
    setIsDragOver(false);
  };

  const handleFile = useCallback((file: File) => {
    setIsParsing(true);
    setError(null);
    setParsedTrips(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const result = parseOrderCSV(text, locations || [], customers || [], tractors || [], drivers || [], trailerTypes || []);
      if (result.success === true) {
        setParsedTrips(result.trips);
        setError(null);
      } else {
        setParsedTrips(null);
        setError(result.error);
      }
      setIsParsing(false);
    };
    reader.onerror = () => {
      setError("Failed to read file");
      setIsParsing(false);
    };
    reader.readAsText(file);
  }, [locations, customers, tractors, drivers, trailerTypes]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleImport = () => {
    if (parsedTrips) {
      onImport(parsedTrips);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          variant="outline"
          onClick={() => { reset(); setOpen(true); }}
          aria-label="Import Orders"
          className="md:px-4 max-md:px-0 max-md:w-10 max-md:h-10"
        >
          <Upload className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Import Orders</span>
        </Button>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Orders from CSV</DialogTitle>
          </DialogHeader>

          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleInputChange}
            />
            <FileUp className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drag & drop a CSV file here, or click to browse
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Required: ship_from_short_code, ship_to_short_code, ready_time, requested_delivery_time, billing_party_short_code (leg 1).
              <br />
              Multi-leg: share a <code>trip_group_id</code> and number rows with <code>leg_sequence</code> starting at 1.
              <br />
              Trip-invariant fields (billing_party_short_code, trailer_id, tender_id, trip_type) must match across all legs of a trip.
              <br />
              All imported trips are created as <strong>Booked</strong>; confirm them later in-app.
            </p>
          </div>

          {isParsing && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Parsing {fileName}…
            </div>
          )}

          {!isParsing && parsedTrips && (
            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              <span>
                <strong>{fileName}</strong> — {parsedTrips.length} trip{parsedTrips.length !== 1 ? "s" : ""} ({parsedTrips.reduce((n, t) => n + (t.kind === "multi" ? t.legs.length : 1), 0)} legs) ready to import
              </span>
            </div>
          )}

          {!isParsing && error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={handleImport}
              disabled={!parsedTrips || isImporting}
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing…
                </>
              ) : (
                "Import"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
